import { apiClient } from './client';

interface RelayStatus {
  active: boolean;
  node?: string;
  epoch?: string;
  rev?: number;
  capabilities?: {
    canDistribute: boolean;
  };
}

interface RelayProvision {
  node: string;
  epoch: string;
  rev: number;
}

interface TraceEntry {
  id: string;
  dir: string;
  /** @deprecated Use amountSub for exact values. */
  amount?: number;
  amountSub?: string;
  memo: string | null;
  ref: string | null;
  status: string;
  peer: { username: string; displayName: string } | null;
  createdAt: string;
}

interface FluxData {
  /** @deprecated Use capacitySub for exact values. */
  capacity?: number;
  capacitySub?: string;
  /** @deprecated Use pendingSub for exact values. */
  pending?: number;
  pendingSub?: string;
  availableSub?: string;
  traces: TraceEntry[];
}

interface RelayBalance {
  /** @deprecated Use confirmedSub for exact values. */
  confirmed?: number;
  /** @deprecated Use pendingSub for exact values. */
  pending?: number;
  /** @deprecated Use availableSub for exact values. */
  available?: number;
  confirmedSub?: string;
  pendingSub?: string;
  availableSub?: string;
}

interface ChallengeData {
  idx: number;
  prevHash: string;
  diff: number;
  reward: number;
  rewardSub?: string;
  minerId: string;
  payloadHash: string;
  payloadVersion: number;
  txHashes: string[];
  txCount: number;
  nBits?: number;
  timestamp?: number;
  headerVersion: number;
}

interface SealResult {
  idx: number;
  hash: string;
  reward: number;
  rewardSub?: string;
  diff: number;
  txCount: number;
}

interface ChainInfo {
  height: number;
  lastHash: string | null;
  diff: number;
  reward: number;
  rewardSub?: string;
  blocks: number;
  pendingTxs: number;
}

interface DistributeResult {
  ok: boolean;
  recipients: number;
  perUserSub: string;
  actualTotalSub: string;
  /** @deprecated Use perUserSub. Present only for safe integer values. */
  perUser?: number;
  /** @deprecated Use actualTotalSub. Present only for safe integer values. */
  actualTotal?: number;
  idempotent?: boolean;
}

interface RelayMutationResult {
  ok: boolean;
  txHash: string;
  status: string;
  idempotent?: boolean;
}

interface DistributePreview {
  recipients: number;
  recipientSetDigest: string;
}

export type { RelayBalance, TraceEntry, FluxData, ChallengeData, SealResult, ChainInfo, DistributeResult, DistributePreview };

export const relayApi = {
  status: () => apiClient.get<RelayStatus>('/api/relay/status').then((r) => r.data),
  provision: () => apiClient.post<RelayProvision>('/api/relay/provision').then((r) => r.data),
  extract: () => apiClient.post<{ fragment: string }>('/api/relay/export').then((r) => r.data),
  balance: () => apiClient.get<RelayBalance>('/api/relay/balance').then((r) => r.data),
  flux: () => apiClient.get<FluxData>('/api/relay/flux').then((r) => r.data),
  traces: (limit = 30, offset = 0) =>
    apiClient.get<TraceEntry[]>('/api/relay/traces', { params: { limit, offset } }).then((r) => r.data),
  dispatch: (target: string, amountSub: string, memo: string | undefined, idempotencyKey: string) =>
    apiClient.post<RelayMutationResult>('/api/relay/dispatch', { target, amountSub, memo }, {
      headers: { 'Idempotency-Key': idempotencyKey },
    }).then((r) => r.data),
  credit: (target: string, amountSub: string, memo: string | undefined, idempotencyKey: string) =>
    apiClient.post<RelayMutationResult>('/api/relay/credit', { target, amountSub, memo }, {
      headers: { 'Idempotency-Key': idempotencyKey },
    }).then((r) => r.data),
  emit: (target: string, amountSub: string, idempotencyKey: string) =>
    apiClient.post<RelayMutationResult>('/api/relay/emit', { target, amountSub }, {
      headers: { 'Idempotency-Key': idempotencyKey },
    }).then((r) => r.data),
  challenge: () => apiClient.get<ChallengeData>('/api/relay/challenge').then((r) => r.data),
  seal: (nonce: string, hash: string, headerData?: {
    extranonce1: string; extranonce2: string; timestamp: number; nonceUint32: number;
  }, snapshot?: {
    payloadHash: string; txHashes: string[]; idx: number; prevHash: string; diff: number;
  }) =>
    apiClient.post<SealResult>('/api/relay/seal', { nonce, hash, ...headerData, ...snapshot }).then((r) => r.data),
  chain: () => apiClient.get<ChainInfo>('/api/relay/chain').then((r) => r.data),
  distribute: (
    amountSub: string,
    memo: string | undefined,
    expectedRecipients: number,
    expectedRecipientSetDigest: string,
    idempotencyKey: string,
  ) =>
    apiClient.post<DistributeResult>('/api/relay/distribute', {
      amountSub,
      memo,
      expectedRecipients,
      expectedRecipientSetDigest,
    }, {
      headers: { 'Idempotency-Key': idempotencyKey },
    }).then((r) => r.data),
  distributePreview: () =>
    apiClient.get<DistributePreview>('/api/relay/distribute/preview').then((r) => r.data),
  mempool: () => apiClient.get('/api/relay/mempool').then((r) => r.data),
};
